/**
 * Football API Service
 * Handles all football-related API calls with caching and error handling
 * @class FootballService
 */
class FootballService {
  constructor() {
    this.baseURL = 'https://v3.football.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY;
    this.debug = false;
    this.cache = {};
    this.maxRetries = 3;
    this.retryDelay = 1000;
    
    if (!this.apiKey) {
      console.warn('🔧 Football API key not found in environment variables');
    }
  }

  /**
   * Manually set API key if environment variables aren't working
   * @param {string} key - The API key to use
   * @returns {void}
   */
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  /**
   * Log messages when debug mode is enabled
   * @private
   * @param {string} message - Log message
   * @param {any} [data=null] - Additional data to log
   * @returns {void}
   */
  log(message, data = null) {
    if (this.debug) {
      console.log(`[Football API] ${message}`, data || ''); 
    }
  }

  /**
   * Generate cache key from endpoint and parameters
   * @private
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Request parameters
   * @returns {string} Cache key
   */
  getCacheKey(endpoint, params) {
    return `${endpoint}_${JSON.stringify(params)}`;
  }

  /**
   * Store data in cache with TTL
   * @private
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {number} [ttl=300000] - Time to live in milliseconds (default: 5 minutes)
   * @returns {void}
   */
  setCache(key, data, ttl = 300000) {
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  /**
   * Retrieve data from cache if not expired
   * @private
   * @param {string} key - Cache key
   * @returns {any|null} Cached data or null if expired/not found
   */
  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;
    
    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }
    
    return cached.data;
  }

  /**
   * Clear all cached data
   * @returns {void}
   */
  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  /**
   * Make API request with retry logic and caching
   * @private
   * @param {string} endpoint - API endpoint
   * @param {Object} [params={}] - Query parameters
   * @param {number} [retryCount=0] - Current retry attempt
   * @returns {Promise<Object>} API response
   * @throws {Error} API or network error
   */
  async makeRequest(endpoint, params = {}, retryCount = 0) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);
    
    if (!this.apiKey) {
      this.log('API Key Debug Info:', {
        envVar: process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarType: typeof process.env.REACT_APP_FOOTBALL_API_KEY,
        envVarLength: process.env.REACT_APP_FOOTBALL_API_KEY ? process.env.REACT_APP_FOOTBALL_API_KEY.length : 0,
        allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      });
      const error = new Error('Football API key not configured');
      error.code = 'API_KEY_MISSING';
      error.details = {
        envVar: process.env.REACT_APP_FOOTBALL_API_KEY,
        availableVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_'))
      };
      throw error;
    }

    const url = new URL(`${this.baseURL}${endpoint}`);
    
    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-host': 'v3.football.api-sports.io',
          'x-rapidapi-key': this.apiKey,
          'Content-Type': 'application/json'
        }
      });

      this.log(`Response status: ${response.status}`);

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`Error response:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      this.log(`Response data:`, data);

      // Check for API errors in response
      if (data.errors && data.errors.length > 0) {
        this.log(`API errors:`, data.errors);
        throw new Error(`API Error: ${data.errors.join(', ')}`);
      }

      // Cache successful responses with different TTLs based on data type
      let ttl = 300000; // 5 minutes default
      if (endpoint.includes('/fixtures')) ttl = 60000; // 1 minute for live data
      if (endpoint.includes('/standings')) ttl = 600000; // 10 minutes for standings
      if (endpoint.includes('/countries') || endpoint.includes('/leagues')) ttl = 86400000; // 24 hours for static data
      if (endpoint.includes('/odds')) ttl = 180000; // 3 minutes for odds

      this.setCache(cacheKey, data, ttl);
      return data;
    } catch (error) {
      this.log(`Request failed:`, error.message);
      
      // Retry logic for network errors
      if (retryCount < this.maxRetries && this.isRetryableError(error)) {
        this.log(`Retrying request (${retryCount + 1}/${this.maxRetries})...`);
        await this.delay(this.retryDelay * (retryCount + 1));
        return this.makeRequest(endpoint, params, retryCount + 1);
      }
      
      // Enhance error with more context
      error.endpoint = endpoint;
      error.params = params;
      error.timestamp = new Date().toISOString();
      
      throw error;
    }
  }

  /**
   * Check if error is retryable
   * @private
   * @param {Error} error - Error object
   * @returns {boolean} Whether the error is retryable
   */
  isRetryableError(error) {
    return error.code === 'ECONNRESET' || 
           error.code === 'ETIMEDOUT' || 
           error.code === 'ENOTFOUND' ||
           error.message.includes('Network') ||
           error.message.includes('fetch');
  }

  /**
   * Delay execution
   * @private
   * @param {number} ms - Milliseconds to delay
   * @returns {Promise<void>}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // CORE ENDPOINTS

  /**
   * Get list of available timezones
   * @returns {Promise<Object>} API response with timezones
   */
  async getTimezones() {
    return await this.makeRequest('/timezone');
  }

  /**
   * Get list of countries
   * @param {string|null} [name=null] - Country name
   * @param {string|null} [code=null] - Country code
   * @param {string|null} [search=null] - Search term
   * @returns {Promise<Object>} API response with countries
   */
  async getCountries(name = null, code = null, search = null) {
    const params = {};
    if (name) params.name = name;
    if (code) params.code = code;
    if (search) params.search = search;
    return await this.makeRequest('/countries', params);
  }

  /**
   * Get list of leagues/competitions
   * @param {number|null} [id=null] - League ID
   * @param {string|null} [name=null] - League name
   * @param {string|null} [country=null] - Country name
   * @param {string|null} [code=null] - Country code
   * @param {number|null} [season=null] - Season year
   * @param {number|null} [team=null] - Team ID
   * @param {string|null} [type=null] - League type
   * @param {string|null} [current=null] - Current leagues only
   * @param {string|null} [search=null] - Search term
   * @param {number|null} [last=null] - Last N leagues
   * @returns {Promise<Object>} API response with leagues
   */
  async getLeagues(id = null, name = null, country = null, code = null, season = null, team = null, type = null, current = null, search = null, last = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (country) params.country = country;
    if (code) params.code = code;
    if (season) params.season = season;
    if (team) params.team = team;
    if (type) params.type = type;
    if (current) params.current = current;
    if (search) params.search = search;
    if (last) params.last = last;
    return await this.makeRequest('/leagues', params);
  }

  // Get available seasons
  async getSeasons() {
    return await this.makeRequest('/leagues/seasons');
  }

  // Get teams
  async getTeams(id = null, name = null, league = null, season = null, country = null, code = null, venue = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (league) params.league = league;
    if (season) params.season = season;
    if (country) params.country = country;
    if (code) params.code = code;
    if (venue) params.venue = venue;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !name && !league && !season && !country && !code && !venue && !search) {
      throw new Error('getTeams requires at least one parameter');
    }

    return await this.makeRequest('/teams', params);
  }

  // Get team statistics
  async getTeamStatistics(league, season, team, date = null) {
    if (!league || !season || !team) {
      throw new Error('getTeamStatistics requires league, season, and team parameters');
    }
    const params = { league, season, team };
    if (date) params.date = date;
    return await this.makeRequest('/teams/statistics', params);
  }

  // Get venues
  async getVenues(id = null, name = null, city = null, country = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (name) params.name = name;
    if (city) params.city = city;
    if (country) params.country = country;
    if (search) params.search = search;

    // This endpoint requires at least one parameter
    if (!id && !name && !city && !country && !search) {
      throw new Error('getVenues requires at least one parameter');
    }

    return await this.makeRequest('/venues', params);
  }

  // FIXTURES & MATCHES

  /**
   * Get fixtures/matches
   * @param {number|null} [id=null] - Fixture ID
   * @param {string|null} [ids=null] - Multiple fixture IDs (comma-separated)
   * @param {string|null} [live=null] - Live fixtures ('all' or league IDs)
   * @param {string|null} [date=null] - Date (YYYY-MM-DD)
   * @param {number|null} [league=null] - League ID
   * @param {number|null} [season=null] - Season year
   * @param {number|null} [team=null] - Team ID
   * @param {number|null} [last=null] - Last N fixtures
   * @param {number|null} [next=null] - Next N fixtures
   * @param {string|null} [from=null] - From date (YYYY-MM-DD)
   * @param {string|null} [to=null] - To date (YYYY-MM-DD)
   * @param {string|null} [round=null] - Round name
   * @param {string|null} [status=null] - Fixture status
   * @param {number|null} [venue=null] - Venue ID
   * @param {string|null} [timezone=null] - Timezone
   * @returns {Promise<Object>} API response with fixtures
   */
  async getFixtures(id = null, ids = null, live = null, date = null, league = null, season = null, team = null, last = null, next = null, from = null, to = null, round = null, status = null, venue = null, timezone = null) {
    const params = {};
    if (id) params.id = id;
    if (ids) params.ids = ids;
    if (live) params.live = live;
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (team) params.team = team;
    if (last) params.last = last;
    if (next) params.next = next;
    if (from) params.from = from;
    if (to) params.to = to;
    if (round) params.round = round;
    if (status) params.status = status;
    if (venue) params.venue = venue;
    if (timezone) params.timezone = timezone;

    // Critical debug logging for 2025 issue
    if (date && date.includes('2025')) {
      console.log('🚨 [FootballService] Making API request for 2025 date:', {
        date,
        season,
        league,
        fullParams: params
      });
    }

    const response = await this.makeRequest('/fixtures', params);

    // Log suspicious responses
    if (date && date.includes('2025') && response?.response?.length > 0) {
      // Check what season the API actually returned
      const returnedSeasons = [...new Set(response.response.map(f => f.league?.season))];
      console.log('🔍 [FootballService] API Response Analysis for 2025:', {
        requestedDate: date,
        requestedSeason: season,
        returnedSeasons: returnedSeasons,
        actualSeasonValues: returnedSeasons[0],  // Show the actual season value
        totalFixtures: response.response.length
      });

      // Check for live games
      const liveGames = response.response.filter(f =>
        ['1H', '2H', 'HT', 'ET', 'BT', 'P', 'SUSP', 'INT', 'LIVE'].includes(f.fixture?.status?.short)
      );

      if (liveGames.length > 0) {
        console.error('🚨🚨 [FootballService] API RETURNED LIVE GAMES FOR FUTURE DATE!', {
          requestedDate: date,
          requestedSeason: season,
          returnedSeasons: returnedSeasons,
          liveGamesCount: liveGames.length,
          exampleGame: {
            fixtureDate: liveGames[0].fixture?.date,
            fixtureTimestamp: liveGames[0].fixture?.timestamp,
            status: liveGames[0].fixture?.status,
            teams: `${liveGames[0].teams?.home?.name} vs ${liveGames[0].teams?.away?.name}`,
            league: liveGames[0].league
          }
        });

        // Check if API is returning wrong year data
        const firstGameDate = new Date(liveGames[0].fixture?.date);
        const firstGameYear = firstGameDate.getFullYear();
        if (firstGameYear !== 2025) {
          console.error('🚨🚨🚨 CRITICAL: API returned fixtures from wrong year!', {
            expected: 2025,
            received: firstGameYear,
            apiSeason: liveGames[0].league?.season
          });
        }
      }
    }

    return response;
  }

  // Get live fixtures
  async getLiveFixtures(leagues = null) {
    return await this.getFixtures(null, null, leagues || 'all');
  }

  // Get fixtures for today
  async getTodayFixtures(timezone = null) {
    const today = new Date().toISOString().split('T')[0];
    return await this.getFixtures(null, null, null, today, null, null, null, null, null, null, null, null, null, null, timezone);
  }

  // Get head to head between teams
  async getHeadToHead(h2h, date = null, league = null, season = null, last = null, next = null, from = null, to = null, status = null, venue = null, timezone = null) {
    if (!h2h) {
      throw new Error('getHeadToHead requires h2h parameter (team1-team2)');
    }
    const params = { h2h };
    if (date) params.date = date;
    if (league) params.league = league;
    if (season) params.season = season;
    if (last) params.last = last;
    if (next) params.next = next;
    if (from) params.from = from;
    if (to) params.to = to;
    if (status) params.status = status;
    if (venue) params.venue = venue;
    if (timezone) params.timezone = timezone;

    return await this.makeRequest('/fixtures/headtohead', params);
  }

  // Get fixture rounds
  async getFixtureRounds(league, season, current = null, dates = null, timezone = null) {
    if (!league || !season) {
      throw new Error('getFixtureRounds requires league and season parameters');
    }
    const params = { league, season };
    if (current) params.current = current;
    if (dates) params.dates = dates;
    if (timezone) params.timezone = timezone;
    return await this.makeRequest('/fixtures/rounds', params);
  }

  // FIXTURE DETAILS

  // Get fixture statistics
  async getFixtureStatistics(fixture, team = null, type = null, half = null) {
    if (!fixture) {
      throw new Error('getFixtureStatistics requires fixture parameter');
    }
    const params = { fixture };
    if (team) params.team = team;
    if (type) params.type = type;
    if (half) params.half = half;
    return await this.makeRequest('/fixtures/statistics', params);
  }

  // Get fixture events
  async getFixtureEvents(fixture, team = null, player = null, type = null) {
    if (!fixture) {
      throw new Error('getFixtureEvents requires fixture parameter');
    }
    const params = { fixture };
    if (team) params.team = team;
    if (player) params.player = player;
    if (type) params.type = type;
    return await this.makeRequest('/fixtures/events', params);
  }

  // Get fixture lineups
  async getFixtureLineups(fixture, team = null, player = null, type = null) {
    if (!fixture) {
      throw new Error('getFixtureLineups requires fixture parameter');
    }
    const params = { fixture };
    if (team) params.team = team;
    if (player) params.player = player;
    if (type) params.type = type;
    return await this.makeRequest('/fixtures/lineups', params);
  }

  // Get fixture player statistics
  async getFixturePlayerStats(fixture, team = null) {
    if (!fixture) {
      throw new Error('getFixturePlayerStats requires fixture parameter');
    }
    const params = { fixture };
    if (team) params.team = team;
    return await this.makeRequest('/fixtures/players', params);
  }

  // STANDINGS

  // Get standings
  async getStandings(league = null, season, team = null) {
    if (!season) {
      throw new Error('getStandings requires season parameter');
    }
    const params = { season };
    if (league) params.league = league;
    if (team) params.team = team;
    return await this.makeRequest('/standings', params);
  }

  // PLAYERS

  // Get player seasons
  async getPlayerSeasons(player = null) {
    const params = {};
    if (player) params.player = player;
    return await this.makeRequest('/players/seasons', params);
  }

  // Get player profiles
  async getPlayerProfiles(player = null, search = null, page = 1) {
    const params = { page };
    if (player) params.player = player;
    if (search) params.search = search;
    return await this.makeRequest('/players/profiles', params);
  }

  // Get player statistics
  async getPlayerStatistics(id = null, team = null, league = null, season = null, search = null, page = 1) {
    const params = { page };
    if (id) params.id = id;
    if (team) params.team = team;
    if (league) params.league = league;
    if (season) params.season = season;
    if (search) params.search = search;
    return await this.makeRequest('/players', params);
  }

  // Get team squads
  async getSquads(team = null, player = null) {
    const params = {};
    if (team) params.team = team;
    if (player) params.player = player;

    // This endpoint requires at least one parameter
    if (!team && !player) {
      throw new Error('getSquads requires either team or player parameter');
    }

    return await this.makeRequest('/players/squads', params);
  }

  // Get player teams
  async getPlayerTeams(player) {
    if (!player) {
      throw new Error('getPlayerTeams requires player parameter');
    }
    return await this.makeRequest('/players/teams', { player });
  }

  // Get player transfers
  async getPlayerTransfers(player) {
    if (!player) {
      throw new Error('getPlayerTransfers requires player parameter');
    }
    return await this.makeRequest('/transfers', { player });
  }

  // Get player trophies
  async getPlayerTrophies(player) {
    if (!player) {
      throw new Error('getPlayerTrophies requires player parameter');
    }
    return await this.makeRequest('/trophies', { player });
  }

  // Get player sidelined information
  async getPlayerSidelined(player) {
    if (!player) {
      throw new Error('getPlayerSidelined requires player parameter');
    }
    return await this.makeRequest('/sidelined', { player });
  }

  // TOP PLAYERS

  // Get top scorers
  async getTopScorers(league, season) {
    if (!league || !season) {
      throw new Error('getTopScorers requires league and season parameters');
    }
    return await this.makeRequest('/players/topscorers', { league, season });
  }

  // Get top assists
  async getTopAssists(league, season) {
    if (!league || !season) {
      throw new Error('getTopAssists requires league and season parameters');
    }
    return await this.makeRequest('/players/topassists', { league, season });
  }

  // Get top yellow cards
  async getTopYellowCards(league, season) {
    if (!league || !season) {
      throw new Error('getTopYellowCards requires league and season parameters');
    }
    return await this.makeRequest('/players/topyellowcards', { league, season });
  }

  // Get top red cards
  async getTopRedCards(league, season) {
    if (!league || !season) {
      throw new Error('getTopRedCards requires league and season parameters');
    }
    return await this.makeRequest('/players/topredcards', { league, season });
  }

  // ODDS ENDPOINTS

  // Get odds for fixtures (pre-match)
  async getOdds(fixture = null, league = null, season = null, date = null, timezone = null, page = 1, bookmaker = null, bet = null) {
    const params = { page };
    if (fixture) params.fixture = fixture;
    if (league) params.league = league;
    if (season) params.season = season;
    if (date) params.date = date;
    if (timezone) params.timezone = timezone;
    if (bookmaker) params.bookmaker = bookmaker;
    if (bet) params.bet = bet;
    return await this.makeRequest('/odds', params);
  }

  // Get live odds (in-play)
  async getLiveOdds(fixture = null, league = null, bet = null) {
    const params = {};
    if (fixture) params.fixture = fixture;
    if (league) params.league = league;
    if (bet) params.bet = bet;
    return await this.makeRequest('/odds/live', params);
  }

  // Get available bookmakers
  async getBookmakers(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bookmakers', params);
  }

  // Get available bet types (pre-match)
  async getBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/bets', params);
  }

  // Get available bet types (live)
  async getLiveBets(id = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (search) params.search = search;
    return await this.makeRequest('/odds/live/bets', params);
  }

  // Get odds mapping
  async getOddsMapping(page = 1) {
    const params = { page };
    return await this.makeRequest('/odds/mapping', params);
  }

  // INJURIES

  // Get injuries
  async getInjuries(league = null, season = null, fixture = null, team = null, player = null, date = null, ids = null, timezone = null) {
    const params = {};
    if (league) params.league = league;
    if (season) params.season = season;
    if (fixture) params.fixture = fixture;
    if (team) params.team = team;
    if (player) params.player = player;
    if (date) params.date = date;
    if (ids) params.ids = ids;
    if (timezone) params.timezone = timezone;

    // This endpoint requires at least one parameter
    if (!league && !season && !fixture && !team && !player && !date && !ids) {
      throw new Error('getInjuries requires at least one parameter');
    }

    return await this.makeRequest('/injuries', params);
  }

  // PREDICTIONS

  // Get predictions
  async getPredictions(fixture) {
    if (!fixture) {
      throw new Error('getPredictions requires fixture parameter');
    }
    return await this.makeRequest('/predictions', { fixture });
  }

  // COACHES

  // Get coaches
  async getCoaches(id = null, team = null, search = null) {
    const params = {};
    if (id) params.id = id;
    if (team) params.team = team;
    if (search) params.search = search;
    return await this.makeRequest('/coachs', params);
  }

  // COMPREHENSIVE DATA AGGREGATION METHODS

  /**
   * Get comprehensive player data combining multiple endpoints
   * @param {number} playerId - The player ID
   * @param {number} [currentSeason=2024] - Current season
   * @returns {Promise<Object>} Comprehensive player data
   * @throws {Error} If player ID is invalid or API error occurs
   */
  async getComprehensivePlayerData(playerId, currentSeason = 2024) {
    try {
      this.log(`Getting comprehensive player data for ID: ${playerId}`);
      
      const [
        playerProfile,
        playerStats,
        playerTeams,
        playerTransfers,
        playerTrophies,
        playerSeasons,
        playerSidelined
      ] = await Promise.allSettled([
        this.getPlayerProfiles(playerId),
        this.getPlayerStatistics(playerId, null, null, currentSeason),
        this.getPlayerTeams(playerId),
        this.getPlayerTransfers(playerId),
        this.getPlayerTrophies(playerId),
        this.getPlayerSeasons(playerId),
        this.getPlayerSidelined(playerId)
      ]);

      const result = {
        profile: playerProfile.status === 'fulfilled' ? playerProfile.value.response?.[0] : null,
        stats: playerStats.status === 'fulfilled' ? playerStats.value.response : [],
        teams: playerTeams.status === 'fulfilled' ? playerTeams.value.response : [],
        transfers: playerTransfers.status === 'fulfilled' ? playerTransfers.value.response : [],
        trophies: playerTrophies.status === 'fulfilled' ? playerTrophies.value.response : [],
        seasons: playerSeasons.status === 'fulfilled' ? playerSeasons.value.response : [],
        sidelined: playerSidelined.status === 'fulfilled' ? playerSidelined.value.response : [],
        lastUpdated: new Date().toISOString()
      };

      this.log(`Comprehensive player data compiled for ID: ${playerId}`);
      return result;
    } catch (error) {
      this.log(`Error getting comprehensive player data for ID: ${playerId}`, error.message);
      throw error;
    }
  }

  /**
   * Get comprehensive fixture odds data from multiple sources
   * @param {number} fixtureId - The fixture ID
   * @returns {Promise<Object>} Comprehensive odds data
   * @throws {Error} If fixture ID is invalid or API error occurs
   */
  async getComprehensiveFixtureOdds(fixtureId) {
    try {
      this.log(`Getting comprehensive fixture odds for ID: ${fixtureId}`);
      
      const [
        preMatchOdds,
        liveOdds,
        bookmakers,
        bets,
        liveBets
      ] = await Promise.allSettled([
        this.getOdds(fixtureId),
        this.getLiveOdds(fixtureId),
        this.getBookmakers(),
        this.getBets(),
        this.getLiveBets()
      ]);

      const result = {
        preMatch: preMatchOdds.status === 'fulfilled' ? preMatchOdds.value.response : [],
        live: liveOdds.status === 'fulfilled' ? liveOdds.value.response : [],
        bookmakers: bookmakers.status === 'fulfilled' ? bookmakers.value.response : [],
        betTypes: bets.status === 'fulfilled' ? bets.value.response : [],
        liveBetTypes: liveBets.status === 'fulfilled' ? liveBets.value.response : [],
        lastUpdated: new Date().toISOString()
      };

      this.log(`Comprehensive odds data compiled for fixture ID: ${fixtureId}`);
      return result;
    } catch (error) {
      this.log(`Error getting comprehensive fixture odds for ID: ${fixtureId}`, error.message);
      throw error;
    }
  }

  /**
   * Get comprehensive fixture data including stats, events, lineups, and odds
   * @param {number} fixtureId - The fixture ID
   * @returns {Promise<Object>} Comprehensive fixture data
   * @throws {Error} If fixture ID is invalid or API error occurs
   */
  async getComprehensiveFixtureData(fixtureId) {
    try {
      this.log(`Getting comprehensive fixture data for ID: ${fixtureId}`);
      
      const [
        fixture,
        fixtureStats,
        fixtureEvents,
        fixtureLineups,
        fixturePlayerStats,
        fixtureOdds
      ] = await Promise.allSettled([
        this.getFixtures(fixtureId),
        this.getFixtureStatistics(fixtureId),
        this.getFixtureEvents(fixtureId),
        this.getFixtureLineups(fixtureId),
        this.getFixturePlayerStats(fixtureId),
        this.getComprehensiveFixtureOdds(fixtureId)
      ]);

      const result = {
        fixture: fixture.status === 'fulfilled' ? fixture.value.response?.[0] : null,
        statistics: fixtureStats.status === 'fulfilled' ? fixtureStats.value.response : [],
        events: fixtureEvents.status === 'fulfilled' ? fixtureEvents.value.response : [],
        lineups: fixtureLineups.status === 'fulfilled' ? fixtureLineups.value.response : [],
        playerStats: fixturePlayerStats.status === 'fulfilled' ? fixturePlayerStats.value.response : [],
        odds: fixtureOdds.status === 'fulfilled' ? fixtureOdds.value : null,
        lastUpdated: new Date().toISOString()
      };

      this.log(`Comprehensive fixture data compiled for ID: ${fixtureId}`);
      return result;
    } catch (error) {
      this.log(`Error getting comprehensive fixture data for ID: ${fixtureId}`, error.message);
      throw error;
    }
  }

  // UTILITY FUNCTIONS

  // Get today's date in YYYY-MM-DD format (user's local timezone)
  getTodayDate() {
    // Get current date in user's timezone
    const date = new Date();
    
    // Format as YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  }

  // Get date range (Eastern Time)
  getDateRange(days = 7, startFromToday = true) {
    const dates = [];
    const startOffset = startFromToday ? 0 : -days;
    
    for (let i = startOffset; i < (startFromToday ? days : 0); i++) {
      // Get current date in Eastern Time
      const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
      const date = new Date(easternTime);
      date.setDate(date.getDate() + i);
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      dates.push(`${year}-${month}-${day}`);
    }
    return dates;
  }

  // Get upcoming dates with labels (Eastern Time)
  getUpcomingDates(days = 7) {
    const dates = [];
    for (let i = 0; i < days; i++) {
      // Get current date in Eastern Time
      const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
      const date = new Date(easternTime);
      date.setDate(date.getDate() + i);
      
      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      let label;
      if (i === 0) label = 'Today (ET)';
      else if (i === 1) label = 'Tomorrow';
      else label = date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        timeZone: 'America/New_York'
      });
      
      dates.push({ value: dateStr, label });
    }
    return dates;
  }

  // Format time with timezone (default to Eastern)
  formatTime(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Format date (default to Eastern)
  formatDate(dateString, timezone = 'America/New_York') {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get full date time
  getFullDateTime(dateString, timezone = 'UTC') {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get status color for fixtures
  getStatusColor(status) {
    const statusMap = {
      'TBD': 'bg-gray-100 text-gray-800',
      'NS': 'bg-blue-100 text-blue-800',
      '1H': 'bg-green-100 text-green-800',
      'HT': 'bg-yellow-100 text-yellow-800',
      '2H': 'bg-green-100 text-green-800',
      'ET': 'bg-orange-100 text-orange-800',
      'BT': 'bg-yellow-100 text-yellow-800',
      'P': 'bg-purple-100 text-purple-800',
      'SUSP': 'bg-red-100 text-red-800',
      'INT': 'bg-yellow-100 text-yellow-800',
      'FT': 'bg-slate-100 text-slate-800',
      'AET': 'bg-slate-100 text-slate-800',
      'PEN': 'bg-slate-100 text-slate-800',
      'PST': 'bg-yellow-100 text-yellow-800',
      'CANC': 'bg-red-100 text-red-800',
      'ABD': 'bg-red-100 text-red-800',
      'AWD': 'bg-gray-100 text-gray-800',
      'WO': 'bg-gray-100 text-gray-800',
      'LIVE': 'bg-red-100 text-red-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  }

  // Calculate betting odds analysis
  calculateOddsAnalysis(odds) {
    if (!odds || !odds.length) return null;

    const analysis = {
      totalBookmakers: odds.length,
      bestOdds: {},
      worstOdds: {},
      averageOdds: {},
      margins: [],
      arbitrageOpportunities: []
    };

    // Process each bet type
    const betTypes = [...new Set(odds.flatMap(book => book.bets.map(bet => bet.name)))];
    
    betTypes.forEach(betType => {
      const betOdds = {};
      
      odds.forEach(bookmaker => {
        const bet = bookmaker.bets.find(b => b.name === betType);
        if (bet) {
          bet.values.forEach(value => {
            if (!betOdds[value.value]) betOdds[value.value] = [];
            betOdds[value.value].push({
              bookmaker: bookmaker.name,
              odd: parseFloat(value.odd)
            });
          });
        }
      });

      // Calculate best/worst odds for each outcome
      Object.keys(betOdds).forEach(outcome => {
        const oddsForOutcome = betOdds[outcome];
        const bestOdd = Math.max(...oddsForOutcome.map(o => o.odd));
        const worstOdd = Math.min(...oddsForOutcome.map(o => o.odd));
        const avgOdd = oddsForOutcome.reduce((sum, o) => sum + o.odd, 0) / oddsForOutcome.length;

        if (!analysis.bestOdds[betType]) analysis.bestOdds[betType] = {};
        if (!analysis.worstOdds[betType]) analysis.worstOdds[betType] = {};
        if (!analysis.averageOdds[betType]) analysis.averageOdds[betType] = {};

        analysis.bestOdds[betType][outcome] = {
          odd: bestOdd,
          bookmaker: oddsForOutcome.find(o => o.odd === bestOdd)?.bookmaker
        };
        analysis.worstOdds[betType][outcome] = {
          odd: worstOdd,
          bookmaker: oddsForOutcome.find(o => o.odd === worstOdd)?.bookmaker
        };
        analysis.averageOdds[betType][outcome] = avgOdd;
      });
    });

    return analysis;
  }

  // Test API connection
  async testConnection() {
    try {
      this.log('Testing API connection...');
      const timezonesResult = await this.getTimezones();
      this.log('Timezones test result:', timezonesResult);
      
      if (timezonesResult && timezonesResult.response) {
        this.log('✅ API connection successful');
        return { success: true, timezones: timezonesResult.response.slice(0, 10) };
      } else {
        throw new Error('Invalid response structure');
      }
    } catch (error) {
      this.log('❌ API connection failed:', error.message);
      return { success: false, error: error.message };
    }
  }

  // Get sample data for testing
  async getSampleData() {
    try {
      this.log('Getting sample data...');
      
      // Try to get countries first
      const countries = await this.getCountries();
      this.log('Sample countries:', countries);
      
      // Try to get seasons
      const seasons = await this.getSeasons();
      this.log('Sample seasons:', seasons);
      
      // Try to get some popular leagues
      const leagues = await this.getLeagues(null, null, null, null, null, null, null, 'true'); // current leagues
      this.log('Sample leagues:', leagues);
      
      return {
        countries: countries.response?.slice(0, 10) || [],
        seasons: seasons.response?.slice(-5) || [], // last 5 seasons
        leagues: leagues.response?.slice(0, 10) || [],
      };
      
    } catch (error) {
      this.log('Error getting sample data:', error.message);
      throw error;
    }
  }

  // Debug method to check environment setup
  debugEnvironment() {
    const envInfo = {
      nodeEnv: process.env.NODE_ENV,
      apiKey: this.apiKey ? `Set (${this.apiKey.length} chars)` : 'Not set',
      apiKeyRaw: process.env.REACT_APP_FOOTBALL_API_KEY,
      allReactAppVars: Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')),
      envCount: Object.keys(process.env).length,
      cacheSize: Object.keys(this.cache).length
    };
    
    console.log('🔍 Football Service Environment Debug:', envInfo);
    return envInfo;
  }
}

// Create singleton instance
const footballService = new FootballService();

// DEBUG: Test methods for live odds debugging - keep for future troubleshooting
// footballService.testLiveOdds = async function() {
//   console.log('🔴 Testing Live Odds...');
//   this.debug = true;
//   
//   // Find live fixtures
//   const liveFixtures = await this.getLiveFixtures();
//   console.log('Live fixtures found:', liveFixtures.response?.length || 0);
//   
//   if (liveFixtures.response && liveFixtures.response.length > 0) {
//     const fixture = liveFixtures.response[0];
//     console.log(`Testing fixture ${fixture.fixture.id}: ${fixture.teams.home.name} vs ${fixture.teams.away.name}`);
//     
//     // Test live odds
//     const liveOdds = await this.getLiveOdds(fixture.fixture.id);
//     console.log('Live odds response:', liveOdds);
//     console.log('Has bookmakers:', liveOdds.response?.[0]?.bookmakers?.length || 0);
//     
//     this.debug = false;
//     return liveOdds;
//   } else {
//     console.log('No live fixtures found to test');
//     this.debug = false;
//     return null;
//   }
// };

// DEBUG: Test specific fixture's live odds - useful for checking why odds aren't displaying
// footballService.testSpecificLiveOdds = async function(fixtureId) {
//   console.log(`🔴 Testing Live Odds for fixture ${fixtureId}...`);
//   this.debug = true;
//   
//   const liveOdds = await this.getLiveOdds(fixtureId);
//   console.log('Live odds response:', liveOdds);
//   console.log('Response structure:', {
//     hasResponse: !!liveOdds.response,
//     responseLength: liveOdds.response?.length,
//     firstItem: liveOdds.response?.[0],
//     bookmakers: liveOdds.response?.[0]?.bookmakers?.length || 0
//   });
//   
//   // If we have odds, show more details
//   if (liveOdds.response && liveOdds.response.length > 0) {
//     console.log('✅ Live odds available!');
//     const firstResponse = liveOdds.response[0];
//     console.log('Full first response structure:', JSON.stringify(firstResponse, null, 2));
//     console.log('Keys in response:', Object.keys(firstResponse));
//     
//     // Check different possible structures
//     if (firstResponse.bookmakers) {
//       console.log('Bookmakers found at .bookmakers:', firstResponse.bookmakers?.map(b => b.name));
//     } else if (firstResponse.odds) {
//       console.log('Odds found at .odds:', firstResponse.odds);
//     } else if (firstResponse.bets) {
//       console.log('Bets found at .bets:', firstResponse.bets);
//     }
//   }
//   
//   this.debug = false;
//   return liveOdds;
// };

// DEBUG: Test which leagues have live odds coverage - essential for understanding API limitations
// footballService.testLiveOddsAvailability = async function() {
//   console.log('🔍 Checking live odds availability across all live fixtures...');
//   
//   const liveFixtures = await this.getLiveFixtures();
//   if (!liveFixtures.response || liveFixtures.response.length === 0) {
//     console.log('No live fixtures found');
//     return [];
//   }
//   
//   console.log(`Found ${liveFixtures.response.length} live fixtures. Testing odds availability...`);
//   
//   const results = [];
//   const leaguesWithOdds = new Set();
//   const leaguesWithoutOdds = new Set();
//   
//   // Test a sample of fixtures (max 10 to avoid rate limiting)
//   const samplesToTest = Math.min(10, liveFixtures.response.length);
//   
//   for (let i = 0; i < samplesToTest; i++) {
//     const fixture = liveFixtures.response[i];
//     const fixtureId = fixture.fixture.id;
//     const leagueName = fixture.league.name;
//     const leagueId = fixture.league.id;
//     
//     try {
//       const odds = await this.getLiveOdds(fixtureId);
//       const hasOdds = odds.response && odds.response.length > 0;
//       
//       if (hasOdds) {
//         leaguesWithOdds.add(`${leagueName} (${leagueId})`);
//         console.log(`✅ ${fixture.teams.home.name} vs ${fixture.teams.away.name} - HAS ODDS`);
//       } else {
//         leaguesWithoutOdds.add(`${leagueName} (${leagueId})`);
//         console.log(`❌ ${fixture.teams.home.name} vs ${fixture.teams.away.name} - NO ODDS`);
//       }
//       
//       results.push({
//         fixtureId,
//         match: `${fixture.teams.home.name} vs ${fixture.teams.away.name}`,
//         league: leagueName,
//         leagueId,
//         hasOdds,
//         bookmakerCount: hasOdds ? (odds.response[0]?.bookmakers?.length || 0) : 0
//       });
//       
//       // Small delay to avoid rate limiting
//       await new Promise(resolve => setTimeout(resolve, 200));
//     } catch (error) {
//       console.error(`Error testing fixture ${fixtureId}:`, error.message);
//     }
//   }
//   
//   console.log('\n📊 Summary:');
//   console.log('Leagues WITH live odds:', Array.from(leaguesWithOdds));
//   console.log('Leagues WITHOUT live odds:', Array.from(leaguesWithoutOdds));
//   console.table(results);
//   
//   return results;
// };

// Add a global method for easy testing in console
if (typeof window !== 'undefined') {
  window.FootballService = footballService;
}

export default footballService;